import { QBusEventMap } from "@otto-ec/event-q-bus";
import { setInitialSessionStartSended } from "./suggestTracking";

/**
 * General interface to tracking
 * i.e. interaction with eventQBus
 */

export type TrackingData = {
  "wk.san_SuggestTime"?: number;
  "wk.san_SearchType"?: string;
  "wk.san_ActiveSearch"?: string;
  "wk.san_SuggestPos"?: string | null;
  "wk.san_SuggestSearchTerm"?: string | null;
  "wk.san_Fallback"?: string;
  "wk.san_CountHits"?: number;
  "wk.san_SearchTerm"?: string;
  "wk.san_SearchWordCount"?: number;
  "wk.san_EmptyStateTime"?: number;
  "wk.san_EmptyStateCount"?: number;
  "wk.san_InitialSearchBoxTime"?: number;
  san_SquirrelSessionStart?: number;
  san_InitialSearch?: string;
  san_Interaction?: string;
  san_SuggestSearchTermLength?: string | null;
  san_SuggestListWordCount?: string | null;
  san_SuggestList?: string | null;
  san_PageLoadComplete?: string;

  [key: string]: string | number | undefined | null;
};

export type HistoryEvent = { historic: boolean } | undefined;

export type SAN_HEADER = "search_close" | "search_open" | "search_x" | "search_cancel";

export type SquirrelEvents = QBusEventMap<{
  "squirrel.search.close": [HistoryEvent?];
  "squirrel.search.hide": [];
  "squirrel.search.open": [HistoryEvent?];
}>;

export type TrackingEvents = QBusEventMap<{
  "tracking.bct.submitEvent": [{ san_Header: SAN_HEADER }];
}>;

export type FTNavEvents = QBusEventMap<{
  "ftnav.global-navigation.opened": [];
  "ftnav.global-navigation.close": [];
}>;

export type FTFindEvents = QBusEventMap<{
  "ftfind.history.merge": [
    {
      topic: "squirrel.search.close";
      data: { historic: boolean };
    }
  ];
  "ftfind.history.push": [
    {
      topic: "squirrel.search.open";
      data: { historic: boolean };
    }
  ];
  "ftfind.history.shift": [];
}>;

export type DataContainerAppilier = (json: TrackingData) => void;

export type BCT = {
  trackOnNextPageImpression: () => void;
  sendEventToTrackingServer: () => void;
};

export type OTracking = {
  bct: BCT;
};

let bct: BCT;
export let _isInitialized: boolean | undefined;

export function isInitialized(): boolean | undefined {
  return _isInitialized;
}

/**
 *
 * @param dataContainer encompassing tracking data
 * @param fn applied with dataContainer
 * @param type the type for debug messages
 */
export function submitToTracking(dataContainer: TrackingData, fn: DataContainerAppilier, type: string) {
  try {
    fn(dataContainer);
    window.o_global.debug.warn(type + " submitting " + JSON.stringify(dataContainer));
  } catch (e) {
    window.o_global.debug.warn(e + "");
  }
}

/**
 *
 * @param trackingData
 */
export function sendTrackingDataForNextPI(trackingData: TrackingData) {
  if (bct && typeof bct.trackOnNextPageImpression === "function") {
    submitToTracking(trackingData, bct.trackOnNextPageImpression, "Track on next PI");
  }
}

/**
 *
 * @param dataContainer
 */
export function sendEventToTracking(dataContainer: TrackingData) {
  if (bct && typeof bct.sendEventToTrackingServer === "function") {
    submitToTracking(dataContainer, bct.sendEventToTrackingServer, "Track event");
  }
}

export function readTrackingFromStorage() {
  let trackingKey: string | null;
  const storage = new window.o_global.Storage(() => window.sessionStorage);

  if (storage.isAvailable) {
    trackingKey = storage.getItem("squirrel_searchTracking");
    if (Boolean(trackingKey).valueOf()) {
      storage.removeItem("squirrel_searchTracking");
      sendEventToTracking(JSON.parse(trackingKey as string));
    }
  }
}

export function init(otracking: OTracking) {
  // store API reference
  bct = otracking.bct;

  readTrackingFromStorage();

  _isInitialized = true;
}

export interface Tracking {
  isInitialized: () => boolean | undefined;
  init: typeof init;
  sendEventToTracking: typeof sendEventToTracking;
  sendTrackingDataForNextPI: typeof sendTrackingDataForNextPI;
}

export const tracking = { isInitialized, init, sendEventToTracking, sendTrackingDataForNextPI };

window.o_global &&
  window.o_global.eventLoader.onReady(1, () => {
    // Deduplication
    if (window.squirrelTrackingInitialized) {
      return;
    }
    window.squirrelTrackingInitialized = true;

    if (bct) {
      init(window.o_tracking);
    } else {
      window.o_global.eventLoader.onLoad(1, () => {
        if (!window.o_util.cookie.get("o_squirrel_sessionStart")) {
          window.o_util.cookie.set("o_squirrel_sessionStart", performance.timing.navigationStart + "");
          setInitialSessionStartSended(false);
        }
        init(window.o_tracking);
      });
    }
  });
